import { useQueryClient } from '@tanstack/react-query';
import { useGoodsSno } from './useGoodsSno';
import { useCallback, useEffect } from 'react';
import { queryKey } from '../constant/queryKey';
import { queryKey as sharedQueryKey } from 'shared/src/constant/queryKey';
import {
  AtomicItemListType,
  goodsService,
  type ScreensReadModel,
  screensService,
} from 'shared/src/api';
import { isNotNil } from 'shared/src/utils';

export const usePreloadQuery = () => {
  const client = useQueryClient();
  const goodsSno = useGoodsSno();

  const prefetchCritical = useCallback(() => {
    client.prefetchQuery({
      queryKey: queryKey.goodsBasic(goodsSno),
      queryFn: () => goodsService.getGoodsBasic(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsDetail(goodsSno),
      queryFn: () => goodsService.getGoodsDetail(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsCodi(goodsSno),
      queryFn: () => goodsService.getCodiGoods(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsMidRelatedRecommend(goodsSno),
      queryFn: () => goodsService.getMidRelatedAdGoods(goodsSno),
    });
  }, [client, goodsSno]);

  const prefetchSecondary = useCallback(() => {
    client.prefetchQuery({
      queryKey: queryKey.goodsOptionsBrief(goodsSno),
      queryFn: () => goodsService.getGoodsOptionsBrief(goodsSno),
    });
    client.prefetchQuery({
      queryKey: queryKey.goodsOptions(goodsSno, { depth: 1 }),
      queryFn: () => goodsService.getGoodsOptions(goodsSno, { depth: 1 }),
    });
    client.prefetchQuery({
      queryKey: queryKey.similarGoods(goodsSno),
      queryFn: () => goodsService.getSimilarGoods(goodsSno),
    });

    client
      .prefetchQuery({
        queryKey: queryKey.goodsAtomicComponents(goodsSno),
        queryFn: () =>
          screensService.get({
            screen: 'GOODS_DETAIL',
            goods_sno: goodsSno,
            next_token: null,
          }),
      })
      .then(() => {
        const atomicComponents = client.getQueryData<ScreensReadModel>(
          queryKey.goodsAtomicComponents(goodsSno),
        );
        if (isNotNil(atomicComponents)) {
          const proxyComponents = atomicComponents.components.filter(
            component => component.type.itemList === AtomicItemListType.Proxy,
          );
          proxyComponents.map(component => {
            const requestUrl = component.entity.body.item.url;
            return client.prefetchQuery({
              queryKey: sharedQueryKey.proxy(requestUrl),
              queryFn: () =>
                screensService.getProxyComponents({ url: requestUrl }),
            });
          });
        }
      });
  }, [client, goodsSno]);

  useEffect(() => {
    prefetchCritical();
    setTimeout(prefetchSecondary, 0);
  }, [prefetchCritical, prefetchSecondary]);
};
